import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, Trash2, X } from 'lucide-react';

const STAGE_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', 
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6B7280'
];

const PipelineFormDialog = ({ 
  open, 
  onOpenChange, 
  pipeline, 
  setPipeline, 
  onSave, 
  onAddStage,
  onRemoveStage,
  onUpdateStage,
  onAddTaskToStage,
  onRemoveTaskFromStage,
  onUpdateTask,
  isEdit = false 
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Pipeline' : 'Create Pipeline'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Pipeline Name *</Label>
              <Input
                placeholder="e.g., T1 Tax Return Process"
                value={pipeline?.name || ''}
                onChange={(e) => setPipeline(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Code (optional)</Label>
              <Input
                placeholder="e.g., T1_TAX"
                value={pipeline?.code || ''}
                onChange={(e) => setPipeline(prev => ({ ...prev, code: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Stages</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onAddStage(setPipeline)}
              >
                <Plus className="h-4 w-4 mr-1" /> Add Stage
              </Button>
            </div>

            {pipeline?.stages?.map((stage, stageIndex) => (
              <div key={stage.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <Input
                      placeholder="Stage name"
                      value={stage.name}
                      onChange={(e) => onUpdateStage(stageIndex, 'name', e.target.value, setPipeline)}
                    />
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-gray-500">Color:</Label>
                      <div className="flex gap-1">
                        {STAGE_COLORS.slice(0, 5).map(color => (
                          <button
                            key={color}
                            type="button"
                            className={`w-6 h-6 rounded-full border-2 ${stage.color === color ? 'border-gray-900' : 'border-transparent'}`}
                            style={{ backgroundColor: color }}
                            onClick={() => onUpdateStage(stageIndex, 'color', color, setPipeline)}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  {pipeline.stages.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500"
                      onClick={() => onRemoveStage(stageIndex, setPipeline)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* Stage Tasks */}
                <div className="pl-4 border-l-2 border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm text-gray-600">Tasks for this stage:</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onAddTaskToStage(stageIndex, setPipeline)}
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add Task
                    </Button>
                  </div>
                  {stage.tasks?.map((task, taskIndex) => (
                    <div key={task.id} className="flex items-center gap-2 mb-2">
                      <Input
                        placeholder="Task name"
                        value={task.name}
                        onChange={(e) => onUpdateTask(stageIndex, taskIndex, 'name', e.target.value, setPipeline)}
                        className="flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500"
                        onClick={() => onRemoveTaskFromStage(stageIndex, taskIndex, setPipeline)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  {(!stage.tasks || stage.tasks.length === 0) && (
                    <p className="text-xs text-gray-400 italic">No tasks defined for this stage</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave}>{isEdit ? 'Update Pipeline' : 'Create Pipeline'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PipelineFormDialog;
